import React from "react";
import { Box, Divider, Stack, ListItemButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronRight } from "@mui/icons-material";
import NavItem from "./NavItem";

export default function RecursiveNav({
  items,
  parentPath = "",
  pathname,
  theme,
  isMasterAuth,
  hideParent,
  expandedKeys = {},
  onToggleExpand,
  isChild = false,
  navigate,
}) {
  const filteredItems = items.filter((item) => !item.requiresMasterAuth || isMasterAuth);
  
  // 첫 번째 자식의 경로를 찾는 헬퍼 함수
  const getFirstChildPath = (item, parentFullPath) => {
    if (!item.child || item.child.length === 0) {
      return null;
    }
    
    // 필터링된 자식 중 첫 번째 항목 찾기
    const firstChild = item.child.find((child) => !child.requiresMasterAuth || isMasterAuth);
    if (!firstChild) {
      return null;
    }
    
    // 자식에 path가 있으면 그것을 사용, 없으면 부모 경로 + 자식 key
    if (firstChild.path) {
      return firstChild.path.startsWith('/') ? firstChild.path : `/${firstChild.path}`;
    }
    
    const childPath = `${parentFullPath}/${firstChild.key}`;
    return childPath.startsWith('/') ? childPath : `/${childPath}`;
  };
  
  return (
    <Stack spacing={0.5}>
      {filteredItems.map((item, index) => {
        const fullPath = item.path || `${parentPath}/${item.key}`;
        const normalizedFullPath = fullPath.startsWith('/') ? fullPath : `/${fullPath}`;
        const isActive = pathname.startsWith(normalizedFullPath);
        const hasChildren = item.child?.length > 0;
        const isExpanded = item.isTopParent 
          ? (expandedKeys[item.key] !== undefined ? expandedKeys[item.key] : false)
          : true;
        const isLastItem = index === filteredItems.length - 1;
        const isChildItem = isChild || hideParent;

        return (
          <React.Fragment key={item.key}>
            {item.isTopParent ? (
              <>
                <ListItemButton
                  onClick={() => {
                    if (hasChildren && onToggleExpand) {
                      onToggleExpand(item.key);
                    }
                    // 첫 번째 자식 경로로 이동
                    if (hasChildren && navigate) {
                      const firstChildPath = getFirstChildPath(item, normalizedFullPath);
                      if (firstChildPath) {
                        navigate(firstChildPath);
                      }
                    }
                  }}
                  sx={{
                    minHeight: 40,
                    borderRadius: 0.75,
                    fontWeight: "fontWeightSemiBold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingX: 2,
                    "&:hover": {
                      background: "linear-gradient(90deg, #84C7C6 0%, #004B49 100%)",
                      borderRadius: 0,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    {item.icon && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: alpha(theme.palette.common.white, 1),
                          fontSize: "16px",
                          "& svg": {
                            fontSize: "16px",
                          },
                        }}
                      >
                        {item.icon}
                      </Box>
                    )}
                    <Box
                      component="span"
                      sx={{
                        color: alpha(theme.palette.common.white, 1),
                        fontSize: "16px",
                        fontWeight: "fontWeightSemiBold",
                      }}
                    >
                      {item.name}
                    </Box>
                    {/* 이벤트 메뉴에 NEW 배지 추가 */}
                    {item.key === 'event' && (
                      <Box
                        sx={{
                          backgroundColor: '#ef4444',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          ml: 1,
                          lineHeight: 1.2,
                        }}
                      >
                        NEW
                      </Box>
                    )}
                  </Box>
                  {hasChildren && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        ml: 1,
                        transition: "transform 0.3s ease-in-out",
                        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                      }}
                    >
                      <ChevronRight
                        sx={{
                          color: alpha(theme.palette.common.white, 0.8),
                          fontSize: "20px",
                        }}
                      />
                    </Box>
                  )}
                </ListItemButton>
                {hasChildren && (
                  <Box
                    sx={{
                      maxHeight: isExpanded ? "1000px" : 0,
                      overflow: "hidden",
                      transition: "max-height 0.7s ease-in-out",
                    }}
                  >
                    {isExpanded && (
                      <>
                        <RecursiveNav
                          hideParent={hideParent}
                          items={item.child}
                          parentPath={normalizedFullPath}
                          pathname={pathname}
                          theme={theme}
                          isMasterAuth={isMasterAuth}
                          expandedKeys={expandedKeys}
                          onToggleExpand={onToggleExpand}
                          isChild={true}
                          navigate={navigate}
                        />
                        {!isLastItem && (
                          <Divider
                            sx={{
                              borderBottomWidth: "1.4px",
                              backgroundColor: alpha(theme.palette.common.white, 0.3),
                              mt: 0.5,
                              mx: 2,
                            }}
                          />
                        )}
                      </>
                    )}
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box
                  className={isActive && isChildItem && item.isMenuItem ? "active-child-gradient" : ""}
                  sx={{}}
                >
                  <NavItem
                    isParent={item.child?.length > 0}
                    item={item}
                    fullPath={normalizedFullPath}
                    isActive={isActive}
                    hideParent={hideParent}
                    isChild={hideParent ? false : true}
                  />
                </Box>
                {hasChildren && (
                  <Box
                    sx={{
                      maxHeight: isExpanded ? "1000px" : 0,
                      overflow: "hidden",
                      transition: "max-height 0.7s ease-in-out",
                      ml: 2,
                      mr: 2,
                    }}
                  >
                    {isExpanded && (
                      <RecursiveNav
                        hideParent={hideParent}
                        items={item.child}
                        parentPath={normalizedFullPath}
                        pathname={pathname}
                        theme={theme}
                        isMasterAuth={isMasterAuth}
                        expandedKeys={expandedKeys}
                        onToggleExpand={onToggleExpand}
                        isChild={hideParent ? false : true}
                        navigate={navigate}
                      />
                    )}
                  </Box>
                )}
              </>
            )}
          </React.Fragment>
        );
      })}
    </Stack>
  );
}
